import { bind, Component, h, signal, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { getElementsFromSlot } from "@/utils/elements";
import { tailwind } from "@/tailwind";

import { DocumentTheme } from "../models";
import { ContentPage } from "./contentPage";
import { CoverPage } from "./coverPage";
import { EndPage } from "./endPage";
import type { ContentType, DocumentPageProps } from "./models";
import style from "./style.css?inline";
import { getElementHeight, splitTextContent } from "./utils";

@tag("nw-doc-page")
export class NWDocPage extends Component<DocumentPageProps> {
  static css = [tailwind, style];
  static propTypes = {
    type: String,
    title: String,
    usersname: String,
    userlink: String,
    usersavatar: String,
    theme: String,
  };

  private processed = signal<boolean>(false);
  protected pagesContainer: HTMLElement | undefined;

  installed(): void {
    const container = this.shadowRoot?.querySelector(".pages-container");
    this.pagesContainer = container instanceof HTMLElement ? container : undefined;

    if (!this.processed.value) {
      setTimeout(() => {
        this.distributeContent();
        this.processed.value = true;
      }, 100);
    }
  }

  @bind
  private distributeContent() {
    if (this.props.type === "cover" || this.props.type === "end") {
      this.processed.value = true;
      return;
    }

    const contents = getElementsFromSlot<HTMLElement>(this.shadowRoot, "content");
    if (!contents) return;

    // Create a temporary page to measure its height
    const tempPage = this.createContentPage(this.props);
    document.body.appendChild(tempPage);

    const contentArea = tempPage.shadowRoot?.querySelector("#container") as HTMLElement;
    const headingElement = tempPage.shadowRoot?.querySelector("#heading") as HTMLElement;
    const headingHeight = headingElement ? headingElement.clientHeight : 0;
    const contentAreaTop = contentArea ? contentArea.offsetTop + headingHeight : 0;
    const contentAreaHeight = tempPage.offsetHeight - contentAreaTop;

    document.body.removeChild(tempPage);

    const PAGE_HEIGHT = contentAreaHeight;

    const pages: HTMLElement[] = [];
    let currentPage = this.createContentPage(this.props);
    let currentHeight = 0;

    contents.forEach(e => {
      const elementHeight = getElementHeight(e);

      if (e.tagName === "NW-DOC-TEXT") {
        // For text elements, we'll try to split them if they don't fit
        if (currentHeight + elementHeight > PAGE_HEIGHT) {
          const availableHeight = PAGE_HEIGHT - currentHeight;

          if (availableHeight < 152) {
            pages.push(currentPage);
            currentPage = this.createContentPage({ ...this.props, title: pages.length > 0 ? "" : this.props.title });
            currentPage.appendChild(e);
            currentHeight = elementHeight;
          } else {
            // Buffer the available height to ensure the next page has more than a few words on it, by increasing the paragraphs moved to the new page
            const { trimmed, remaining } = splitTextContent(e, availableHeight - 40);
            const hasTrimmedContent = !isEmpty(trimmed);

            if (!hasTrimmedContent) {
              // Move to the next page
              pages.push(currentPage);
              currentPage = this.createContentPage({ ...this.props, title: pages.length > 0 ? "" : this.props.title });
              currentPage.appendChild(e);
              currentHeight = elementHeight;
            } else {
              // this means that the text element is too large to fit on the page
              const trimmedElement = document.createElement("nw-doc-text");
              trimmedElement.setAttribute("theme", this.props.theme);
              trimmedElement.setAttribute("placeholder", trimmed);
              trimmedElement.setAttribute("title", e.getAttribute("title") ?? "placeholder");

              currentPage.appendChild(trimmedElement);
              currentHeight += getElementHeight(trimmedElement);
              e.remove();

              if (!isEmpty(remaining)) {
                pages.push(currentPage);

                currentPage = this.createContentPage({
                  ...this.props,
                  title: pages.length > 0 ? "" : this.props.title,
                });

                const remainingElement = document.createElement("nw-doc-text");
                remainingElement.setAttribute("theme", this.props.theme);
                remainingElement.setAttribute("placeholder", remaining);

                currentPage.appendChild(remainingElement);
                currentHeight = elementHeight;
              }
            }
          }
        } else {
          // Text fits on current page
          currentPage.appendChild(e);
          currentHeight += elementHeight;
        }
      } else {
        if (currentHeight + elementHeight <= PAGE_HEIGHT) {
          currentPage.appendChild(e);
          currentHeight += elementHeight;
        } else {
          // Element doesn't fit on current page
          pages.push(currentPage);
          currentPage = this.createContentPage({ ...this.props, title: pages.length > 0 ? "" : this.props.title });
          currentPage.appendChild(e);
          currentHeight = elementHeight;
        }
      }
    });

    pages.push(currentPage);
    this.rootElement?.append(...pages);
  }

  private createContentPage(props: DocumentPageProps) {
    const page = document.createElement("nw-doc-page-content");

    page.setAttribute("type", "content");
    page.setAttribute("theme", props.theme || DocumentTheme.LIGHT);
    if (!isEmpty(props.title)) {
      page.setAttribute("title", props.title);
    }
    page.setAttribute("usersname", props.usersname);
    page.setAttribute("userlink", props.userlink);
    page.setAttribute("usersavatar", props.usersavatar);

    return page;
  }

  getContentByType(type: ContentType) {
    switch (type) {
      case "cover":
        return <CoverPage {...this.props} />;
      case "end":
        return <EndPage {...this.props} />;
      default:
        return <ContentPage {...this.props} />;
    }
  }

  render() {
    if (this.props.type === "cover") {
      return this.getContentByType(this.props.type);
    }

    if (this.props.type === "end") {
      return this.getContentByType(this.props.type);
    }

    return (
      <div class="pages-container">
        <slot id="content"></slot>
      </div>
    );
  }
}
